import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import { BotBadge } from '../../components/ui';
import { initial, isOnline, presenceLabel } from '../../lib/format';

const DECISION_DISTANCE = 105;
const TAP_TOLERANCE = 8;
const FLY_OUT_MS = 200;

export default function SwipeCard({ person, depth, forcedDecision, onDecide, onOpenProfile }) {
  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [flyOut, setFlyOut] = useState(null);
  const start = useRef(null);
  const moved = useRef(0);
  const settled = useRef(false);

  const isTop = depth === 0;
  const photoCount = (person.photos || []).length;

  const finish = useCallback(
    (decision) => {
      if (settled.current) return;
      settled.current = true;
      setFlyOut(decision);
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        onDecide(person, decision);
      } else {
        window.setTimeout(() => onDecide(person, decision), FLY_OUT_MS);
      }
    },
    [onDecide, person]
  );

  // Decisions made with the action buttons / keyboard fly the card out the same way a drag does.
  useEffect(() => {
    if (isTop && forcedDecision) finish(forcedDecision);
  }, [isTop, forcedDecision, finish]);

  const onPointerDown = (e) => {
    if (!isTop || flyOut) return;
    start.current = { x: e.clientX, y: e.clientY };
    moved.current = 0;
    setDrag({ x: 0, y: 0, dragging: true });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!start.current) return;
    const x = e.clientX - start.current.x;
    const y = e.clientY - start.current.y;
    moved.current = Math.max(moved.current, Math.abs(x) + Math.abs(y));
    setDrag({ x, y, dragging: true });
  };

  const onPointerUp = () => {
    if (!start.current) return;
    const { x } = drag;
    const wasTap = moved.current < TAP_TOLERANCE;
    start.current = null;

    if (wasTap) {
      setDrag({ x: 0, y: 0, dragging: false });
      onOpenProfile(person);
      return;
    }
    if (x > DECISION_DISTANCE) {
      finish('like');
      return;
    }
    if (x < -DECISION_DISTANCE) {
      finish('pass');
      return;
    }
    setDrag({ x: 0, y: 0, dragging: false });
  };

  let transform;
  if (flyOut) {
    const direction = flyOut === 'like' ? 1 : -1;
    transform = `translate(${direction * 520}px, 40px) rotate(${direction * 22}deg)`;
  } else if (isTop) {
    transform = `translate(${drag.x}px, ${drag.y}px) rotate(${drag.x * 0.05}deg)`;
  } else {
    transform = `translateY(${depth * -10}px) scale(${1 - depth * 0.045})`;
  }

  const intent = Math.min(Math.abs(drag.x) / DECISION_DISTANCE, 1);

  return (
    <div
      className={`swipe-card ${isTop ? 'swipe-card-top' : 'swipe-card-behind'}`}
      style={{
        transform,
        zIndex: 10 - depth,
        opacity: isTop ? 1 : 0.55,
        transition: drag.dragging ? 'none' : `transform ${FLY_OUT_MS}ms ease-out, opacity 0.2s ease`,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-hidden={!isTop}
    >
      <div className="swipe-card-media">
        {person.profileImage ? (
          <img src={person.profileImage} alt={person.username} draggable={false} />
        ) : (
          <div className="swipe-card-fallback">{initial(person.username)}</div>
        )}
      </div>
      <div className="swipe-card-scrim" />

      {isTop && drag.x > 12 && (
        <div className="swipe-stamp swipe-stamp-like" style={{ opacity: intent }}>
          Like
        </div>
      )}
      {isTop && drag.x < -12 && (
        <div className="swipe-stamp swipe-stamp-nope" style={{ opacity: intent }}>
          Nope
        </div>
      )}

      <div className="swipe-card-info">
        <div className="swipe-card-name">
          {person.username}
          {person.isBot && <BotBadge />}
        </div>
        {person.bio && <p className="swipe-card-bio">{person.bio}</p>}
        <div className="swipe-card-meta">
          <span className="presence">
            <span className={`presence-dot${isOnline(person.lastSeen) ? ' online' : ''}`} />
            {presenceLabel(person.lastSeen)}
          </span>
          {photoCount > 0 && (
            <span className="swipe-card-photocount">
              <Icon name="image" size={14} />
              {photoCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
